import React, {useEffect, useState, useRef} from 'react'
import {useSelector} from 'react-redux';
import DataTable from 'react-data-table-component';
import {get_beneficiary_reimbursement, getBeneficiaryApprovalStatus, deleteReimbursement} from "../../helper/Api";
import { AddCommasToAmount } from "../../helper/Utils";
import {AiOutlineEye} from 'react-icons/ai';
import {Link} from 'react-router-dom';
import {useParams} from 'react-router-dom';
import { FaEdit } from 'react-icons/fa';
import { BiTrash } from 'react-icons/bi';
import { MdOutlineFactCheck } from 'react-icons/md';
import { Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, Button } from "@mui/material";
import StatusModal from './StatusModal';
import './BeneficiaryReimbursementList.css';
import { toast } from "react-toastify";
import { DepartmentDistrictBadge } from '../Tools/DepartmentIdentityCell';
import moment from 'moment';

const getSchemeDisplayName = (row) => {
    const code = row.code || '';
    const name = row.name || '';
    if (!code) return name;
    if (name.startsWith(`${code}-`)) return name.slice(code.length + 1);
    if (name.startsWith(code)) return name.slice(code.length).replace(/^-/, '');
    return name;
};

const getSubheadDisplayName = (row) => {
    const code = row.subhead_code || '';
    const name = row.subhead_name || '';
    if (!code) return name;
    if (name.startsWith(`${code}-`)) return name.slice(code.length + 1);
    if (name.startsWith(code)) return name.slice(code.length).replace(/^-/, '');
    return name;
};

const getApprovalBadgeClass = (approval) => {
    if (approval === null || approval === undefined || approval === '') {
        return 'bg-warning-20';
    }
    const val = Number(approval);
    if (val === 1) return 'bg-success-20';
    if (val === 0 || val === 2) return 'bg-danger-20';
    return 'bg-warning-20';
};

const getApprovalIconClass = (approval) => {
    if (approval === null || approval === undefined || approval === '') {
        return 'icon-warning_amber text-warning-100';
    }
    const val = Number(approval);
    if (val === 1) return 'icon-verified text-success-100';
    if (val === 0 || val === 2) return 'icon-warning_amber text-danger-100';
    return 'icon-warning_amber text-warning-100';
};

const PaymentStatusCell = ({ l2Approval, l3Approval, showL2 = true }) => (
    <div className="br-payment-status-cell">
        {showL2 ? (
            <span className={`br-payment-status-badge ${getApprovalBadgeClass(l2Approval)}`}>
                L2 <i className={`${getApprovalIconClass(l2Approval)} fs-16px`} />
            </span>
        ) : null}
        <span className={`br-payment-status-badge ${getApprovalBadgeClass(l3Approval)}`}>
            L3 <i className={`${getApprovalIconClass(l3Approval)} fs-16px`} />
        </span>
    </div>
);

const formatCreationDate = (value) => {
    if (!value) return null;
    const parsed = moment(value, ['YYYY-MM-DD HH:mm:ss', 'YYYY-MM-DDTHH:mm:ssZ', moment.ISO_8601], true);
    if (!parsed.isValid()) return null;
    return {
        date: parsed.format('DD-MM-YYYY'),
        time: parsed.format('hh:mm A'),
    };
};

export default function BeneficiaryReimbursementList(filterData) {
    const [showPreview, setShowPreview]=useState(false);
    const {authData} = useSelector((state) => state.authData);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [data, setData] = useState([]);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [rowPendingDelete, setRowPendingDelete] = useState(null);
    const {listType} = useParams();
    const approvalLogsData = useRef({
        ownData: {},
        logs: []
    });

    const formatAmount = (value) => {
        if (value === null || value === undefined || value === '') return '-';
        return AddCommasToAmount(value);
    };

    const customStyles = {
        headCells: {
            style: {
                fontWeight: "bold",
                fontSize: "14px"
            },
        },
    };
    let paymentColumns = [{
        name: 'Payment Status',
        cell: (row) => (
            <PaymentStatusCell
                l2Approval={row.l2_payment_approval}
                l3Approval={row.l3_payment_approval}
                showL2={row.approval_type !== 'single'}
            />
        ),
        center: true,
        minWidth: '160px',
    }];

    if(listType && listType == 'approval') {
        paymentColumns = [];
    }

    const viewStatusLogs = async (row) => {
        approvalLogsData.current.ownData = {
            l2_approval: row.l2_approval,
            l3_approval: row.l3_approval,
            department_level: row.department_level,
            reimbursement_order_no: row.reimbursement_order_no,
        };
        const result = await getBeneficiaryApprovalStatus(row.id);
        if(result.data.status) {
            if(result.data.result.length) {      
                let data = result.data.result;
                approvalLogsData.current.logs = data;
            }
        }
        setShowPreview(true);
    }

    const openDeleteConfirm = (row) => {
        setRowPendingDelete(row);
        setDeleteConfirmOpen(true);
    };

    const closeDeleteConfirm = () => {
        setDeleteConfirmOpen(false);
        setRowPendingDelete(null);
    };

    const confirmDelete = async () => {
        if (!rowPendingDelete?.id) return;
        try {
            const res = await deleteReimbursement(rowPendingDelete.id);
            if (res?.data?.status) {
                toast.success(res?.data?.message || "Deleted successfully.", {
                    position: toast.POSITION.TOP_CENTER,
                });
                closeDeleteConfirm();
                await fetchChallans();
            } else {
                toast.error(res?.data?.message || "Delete failed.", {
                    position: toast.POSITION.TOP_CENTER,
                });
            }
        } catch (err) {
            toast.error(
                err?.response?.data?.message || err?.message || "Delete failed.",
                { position: toast.POSITION.TOP_CENTER }
            );
        }
    };

    const columns = [
        {
            name: 'Actions',
            cell: (row) => {
                const canDelete =
                    String(authData?.user?.user_type || '').toLowerCase() === 'l1' &&
                    ((String(row?.l2_approval) === '0' || Number(row?.l2_approval) === 0) ||
                        (String(row?.l3_approval) === '0' || Number(row?.l3_approval) === 0));
                const hasRejection = row.rejection && row.rejection !== '';
                const canEdit =
                    authData.user.user_type === 'l1' &&
                    authData.user.department_id == row.department_id &&
                    (row.l2_approval == 0 || row.l3_approval == 0 || row.rejection);

                return (
                    <div className="br-actions">
                        <Tooltip title="View Details" arrow placement="top">
                            <span className="br-action-tooltip-wrap">
                                <Link
                                    to={`/add-beneficiary-reimbursement/${row.id}`}
                                    className="br-action-btn br-action-btn--view"
                                >
                                    <AiOutlineEye size={16} />
                                </Link>
                            </span>
                        </Tooltip>
                        <Tooltip title="Approval Status" arrow placement="top">
                            <span className="br-action-tooltip-wrap">
                                <button
                                    type="button"
                                    className={`br-action-btn br-action-btn--status${hasRejection ? ' br-action-btn--status-rejected' : ''}`}
                                    onClick={() => viewStatusLogs(row)}
                                >
                                    <MdOutlineFactCheck size={16} />
                                </button>
                            </span>
                        </Tooltip>
                        {canEdit ? (
                            <Tooltip title="Edit" arrow placement="top">
                                <span className="br-action-tooltip-wrap">
                                    <Link
                                        to={`/edit-beneficiary-reimbursement/${row.id}`}
                                        className="br-action-btn br-action-btn--edit"
                                    >
                                        <FaEdit size={15} />
                                    </Link>
                                </span>
                            </Tooltip>
                        ) : null}
                        {canDelete ? (
                            <Tooltip title="Delete" arrow placement="top">
                                <span className="br-action-tooltip-wrap">
                                    <button
                                        type="button"
                                        className="br-action-btn br-action-btn--delete"
                                        onClick={() => openDeleteConfirm(row)}
                                        aria-label={`Delete reimbursement ${row.reimbursement_order_no || row.id}`}
                                    >
                                        <BiTrash size={16} />
                                    </button>
                                </span>
                            </Tooltip>
                        ) : null}
                    </div>
                );
            },
            wrap: true,
            width: "100px",
            center: true
        },
        {
            name: 'Department',
            cell: (row) => (
                <div className="br-dept-cell">
                    <span className="br-dept-name">{row.department_name || '-'}</span>
                    {row.department_level ? (
                        <span className="br-dept-level-badge">{row.department_level}</span>
                    ) : null}
                    <DepartmentDistrictBadge row={row} />
                </div>
            ),
            wrap: true,
            minWidth: '200px',
        },
        {
            name: 'Order',
            cell: (row) => (
                <div className="br-order-cell">
                    {row.reimbursement_order_no ? (
                        <span className="br-order-no-badge">{row.reimbursement_order_no}</span>
                    ) : (
                        <span className="br-order-no-badge">-</span>
                    )}
                    {row.reimbursement_order_date ? (
                        <span className="br-order-date">{row.reimbursement_order_date}</span>
                    ) : null}
                </div>
            ),
            wrap: true,
            minWidth: '200px',
        },
        {
            name: 'Order Amount',
            selector: (row) => formatAmount(row.reimbursement_order_value),
            wrap: true,
            width: "150px"
        },
        {
            name: 'Scheme',
            cell: (row) => {
                const schemeName = getSchemeDisplayName(row);
                return (
                    <div className="br-scheme-cell">
                        <span className="br-scheme-detail">
                            <strong>{row.code}</strong>
                            {schemeName ? ` - ${schemeName}` : ''}
                        </span>
                        {row.type ? (
                            <span className="br-scheme-type-badge">{row.type}</span>
                        ) : null}
                    </div>
                );
            },
            wrap: true,
            minWidth: '320px',
        },
        {
            name: 'Subhead',
            cell: (row) => {
                const subheadName = getSubheadDisplayName(row);
                return (
                    <span>
                        <strong>{row.subhead_code}</strong>
                        {subheadName ? ` - ${subheadName}` : ''}
                    </span>
                );
            },
            wrap: true,
            minWidth: '280px',
        },
        {
            name: 'Creation Date',
            cell: (row) => {
                const formatted = formatCreationDate(row.created_at);
                if (!formatted) return '-';
                return (
                    <div className="br-created-cell">
                        <span className="br-created-date">{formatted.date}</span>
                        <span className="br-created-time">{formatted.time}</span>
                    </div>
                );
            },
            wrap: true,
            minWidth: '130px',
        },
        ...paymentColumns
    ];

    const handleChangeRowsPerPage = (currentRowsPerPage, currentPage) => {
        setRowsPerPage(currentRowsPerPage);
        setCurrentPage(1); // Reset to the first page when rows per page changes
    };

    const fetchChallans = async () => {
        try {
            const resp = await get_beneficiary_reimbursement({
                from_date: filterData?.fromDate || '',
                to_date: filterData?.toDate || '',
                search_str: '',
                dateColumn: 'reimbursement_order_date',
                department_id: filterData?.departmentId || authData?.user?.department_id || '',
                listType: listType == 'dbt_payment' ? '' : listType,
                is_all: filterData?.is_all || '',
                district_id: filterData?.districtId || '',
            });
            if (resp.data.status) {
                setData(resp.data.result);
            }
        } catch (error) {
            setData([]);
        }
    }

    useEffect(() => {
        fetchChallans();
    }, [filterData.fromDate, filterData.toDate, filterData.departmentId, listType, filterData.is_all, filterData.districtId]);



    return (
        <div>
            <StatusModal showPreview={showPreview} setShowPreview={setShowPreview} approvalLogs={approvalLogsData?.current?.logs} data={approvalLogsData?.current?.ownData}/>
            <Dialog
                open={deleteConfirmOpen}
                onClose={closeDeleteConfirm}
                maxWidth="xs"
                fullWidth
                aria-labelledby="br-delete-title"
            >
                <DialogTitle id="br-delete-title">Delete reimbursement</DialogTitle>
                <DialogContent dividers>
                    <p className="mb-0" style={{ fontSize: 15, lineHeight: 1.5 }}>
                        Are you sure you want to delete this reimbursement invoice? This action cannot be undone.
                    </p>
                    {rowPendingDelete && (
                        <p className="mt-2 mb-0 text-muted small">
                            Order No.: <strong>{rowPendingDelete.reimbursement_order_no ?? "—"}</strong>
                        </p>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 2.5, pb: 2 }}>
                    <Button onClick={closeDeleteConfirm} color="inherit">
                        Cancel
                    </Button>
                    <Button onClick={confirmDelete} variant="contained" color="error">
                        Yes, delete
                    </Button>
                </DialogActions>
            </Dialog>
            <div className="beneficiary-reimbursement-datatable-root">
                <DataTable
                    customStyles={customStyles}
                    className="dataTables_wrapper"
                    columns={columns}
                    data={data}
                    fixedHeader
                    fixedHeaderScrollHeight="600px"
                    pagination
                    paginationPerPage={rowsPerPage}
                    paginationTotalRows={data.length}
                    paginationRowsPerPageOptions={[10, 25, 50, 100]}
                    onChangeRowsPerPage={handleChangeRowsPerPage}
                />
            </div>
        </div>
    )

}